import Backdrop from "@components/Backdrop"
import ProductSelector from "@components/ProductSelector"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents from "@hooks/useEnhancedEcommerceEvents"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { INVENTORY_ITEM_TRACKED } from "@hooks/useMultiWarehouseInventory"
import {
  AddToCartPayload,
  AutomaticDiscountAllocations,
  AutomaticDiscountData,
  CartItem,
  DiscountedProductsData,
} from "@redux/types"
import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import { getIn } from "@utils/index"
import { getLocalStorage } from "@utils/storage"
import _ from "lodash-es"
import React from "react"
import styled, { css } from "styled-components"

import AddDiscount from "./AddDiscount"
import Body from "./Body"
import DiscountButton from "./DiscountButton"
import EditCartItem from "./EditCartItem"
import EditDiscount from "./EditDiscount"
import EmptyCart from "./EmptyCart"
import Footer from "./Footer"
import Header from "./Header"
import { RecentItemProps } from "./RecentItem"
import { DiscountResponseProps } from "./types"

const openProps = css`
  opacity: 1;
  transform: translateX(0);
  transition:
    opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
`

const disableClicksProps = css`
  a,
  button {
    pointer-events: none;
  }
`

const Container = styled.div<{ $disableClicks: boolean; $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  overflow-x: auto;
  width: 100%;
  padding-top: 73px;
  opacity: 0;
  transform: translateX(100%);
  transition:
    opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  cursor: default;

  ${({ $isOpen }) => ($isOpen ? openProps : "")}
  ${({ $disableClicks }) => ($disableClicks ? disableClicksProps : "")}

  @media ${breakpoints.laptop} {
    max-width: 1088px;
    padding-top: 115px;
  }
`

const Content = styled.div`
  overflow: hidden;
  overflow-y: auto;
  height: 100%;

  @media ${breakpoints.laptop} {
    align-items: flex-start;
    justify-content: flex-start;
    display: flex;
  }
`

export interface CartProps {
  addToCart: (payload: AddToCartPayload) => void
  automaticDiscountAllocations: AutomaticDiscountAllocations
  automaticDiscountData: any
  automaticDiscounts: AutomaticDiscountData[]
  cartCount: number
  checkoutLink?: string
  couponCodeApplied?: string
  customerLocation: any
  discountedProductsData: DiscountedProductsData
  discountedProductsInventoryData: any
  fetchDiscountInventoryForProduct: (productContentfulId: string) => void
  isOpen: boolean
  items: Array<CartItem>
  onRemoveDiscount: () => void
  onRequestClose: () => void
  onUpdateCartItem: ({
    cartItem,
    lineIndex,
  }: {
    cartItem: CartItem
    lineIndex: number
  }) => void
  onUpdateDiscount: (discount: string | undefined) => DiscountResponseProps
  recentItems: Array<RecentItemProps>
  showPromoToast: boolean
  subtotal: string
  total: string
}

const Cart: React.FC<CartProps> = ({
  addToCart,
  automaticDiscountAllocations,
  automaticDiscountData,
  automaticDiscounts,
  cartCount,
  checkoutLink,
  couponCodeApplied,
  customerLocation,
  discountedProductsData,
  discountedProductsInventoryData,
  fetchDiscountInventoryForProduct,
  isOpen,
  items,
  onRemoveDiscount,
  onRequestClose,
  onUpdateCartItem,
  onUpdateDiscount,
  recentItems,
  showPromoToast,
  subtotal,
  total,
}) => {
  const cartRef: any = React.useRef(null)
  const addDiscountRef: any = React.useRef(null)
  const editDiscountRef: any = React.useRef(null)
  const editCartItemRef: any = React.useRef(null)
  const productSelectorRef: any = React.useRef(null)

  const [delta, setDelta] = React.useState<number>(0)
  const [cartItemToUpdate, setCartItemToUpdate] = React.useState<
    { cartItem: CartItem; lineIndex: number } | undefined
  >(undefined)

  const { onRemoveProductFromCart } = useEnhancedEcommerceEvents()
  const { elevarOnAddToCart, elevarOnRemoveFromCart, elevarOnViewCart } =
    useElevarEvents()

  React.useEffect(() => {
    if (isOpen) {
      elevarOnViewCart(
        items.map((item, i) => ({
          brand: "Urban Armor Gear",
          device: item.device,
          deviceType: item.deviceType,
          position: String(i + 1),
          price: item.price,
          productId: item.shopifyProductId,
          productName: item.productName,
          quantity: String(item.quantity),
          sku: item.sku,
          variantId: item.shopifyVariantId,
          variantName: item.variantName,
        })) as Array<ElevarProductProps>,
        total
      )
    }
  }, [isOpen])

  // Fetch discount inventory for products in cart when cart opens or items change
  React.useEffect(() => {
    if (isOpen && items.length > 0 && discountedProductsData) {
      // Get unique contentful product IDs from cart items
      const uniqueProductIds = _.uniq(
        items.map((item) => item.contentfulProductId).filter(Boolean)
      )

      // Fetch discount inventory for each unique product in cart
      // This will accumulate inventory for all relevant discount products
      uniqueProductIds.forEach((productId) => {
        if (productId) {
          fetchDiscountInventoryForProduct(productId)
        }
      })
    }
  }, [isOpen, items, discountedProductsData, fetchDiscountInventoryForProduct])

  const onRemoveCartItem = (cartItem: CartItem, lineIndex: number): void => {
    onUpdateCartItem({
      cartItem: {
        ...cartItem,
        quantity: 0,
      },
      lineIndex,
    })

    onRemoveProductFromCart({
      brand: cartItem.brand,
      device: cartItem.device,
      deviceType: cartItem.deviceType,
      price: cartItem.price,
      productId: cartItem.shopifyProductId,
      productName: cartItem.productName,
      quantity: 0,
      sku: cartItem.sku,
      variantName: cartItem.variantName,
    })

    const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
    const list = selectedProducts[cartItem.shopifyProductId] || ""

    elevarOnRemoveFromCart(
      {
        brand: "Urban Armor Gear",
        device: cartItem.device,
        deviceType: cartItem.deviceType,
        image: cartItem.image,
        list,
        price: cartItem.price,
        productId: cartItem.shopifyProductId,
        productName: cartItem.productName,
        quantity: cartItem.quantity,
        sku: cartItem.sku,
        variantId: cartItem.shopifyVariantId,
        variantName: cartItem.variantName,
      },
      list
    )
  }

  const handleOnRemoveCartItemMobile = () => {
    if (cartItemToUpdate) {
      onRemoveCartItem(cartItemToUpdate.cartItem, cartItemToUpdate.lineIndex)
    }
    setDelta(0)
    setCartItemToUpdate(undefined)
  }

  const handleOnUpdateCartItemMobile = () => {
    if (cartItemToUpdate?.cartItem && delta !== 0) {
      const updatedQuantity = cartItemToUpdate.cartItem.quantity + delta
      const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
      const list =
        selectedProducts[cartItemToUpdate.cartItem.shopifyProductId] || ""
      const elevarProduct = {
        brand: "Urban Armor Gear",
        device: cartItemToUpdate.cartItem.device,
        deviceType: cartItemToUpdate.cartItem.deviceType,
        image: cartItemToUpdate.cartItem.image,
        list,
        price: cartItemToUpdate.cartItem.price,
        productId: cartItemToUpdate.cartItem.shopifyProductId,
        productName: cartItemToUpdate.cartItem.productName,
        quantity: Math.abs(delta),
        sku: cartItemToUpdate.cartItem.sku,
        variantId: cartItemToUpdate.cartItem.shopifyVariantId,
        variantName: cartItemToUpdate.cartItem.variantName,
      }

      if (delta > 0) {
        elevarOnAddToCart(elevarProduct, list)
      } else {
        elevarOnRemoveFromCart(elevarProduct, list)
      }

      onUpdateCartItem({
        cartItem: { ...cartItemToUpdate.cartItem, quantity: updatedQuantity },
        lineIndex: cartItemToUpdate.lineIndex,
      })
    }
    setDelta(0)
    setCartItemToUpdate(undefined)
  }

  const [showAddDiscount, setShowAddDiscount] = React.useState<boolean>(false)

  const openAddDiscount = () => {
    setShowAddDiscount(true)
  }

  const closeAddDiscount = () => {
    setShowAddDiscount(false)
  }

  const onAddDiscountSuccess = () => {
    closeAddDiscount()
  }

  const [showEditDiscount, setShowEditDiscount] = React.useState<boolean>(false)

  const openEditDiscount = () => {
    setShowEditDiscount(true)
  }

  const closeEditDiscount = () => {
    setShowEditDiscount(false)
  }

  const handleOnRemoveDiscount = () => {
    closeEditDiscount()
    onRemoveDiscount()
  }

  useModalBackgroundClose({
    excludeRefs: [
      addDiscountRef,
      editDiscountRef,
      editCartItemRef,
      productSelectorRef,
    ],
    isOpen,
    modalRef: cartRef,
    onRequestClose,
  })

  const [productSelectorProducts, setProductSelectorProducts] =
    React.useState<any>([])
  const [productSelectorDiscountConfig, setProductSelectorDiscountConfig] =
    React.useState<any>({})

  const closeProductSelector = () => {
    setProductSelectorProducts([])
    setProductSelectorDiscountConfig({})
  }

  const { customerCountryCode } = customerLocation

  const availableSkusByRegion = React.useMemo(() => {
    const discountVariants = _.flatten(
      Object.values(discountedProductsData).map(({ variants }) => variants)
    )
    const availableSkus: any = []

    discountVariants.forEach(({ sku }) => {
      const tracked = getIn(
        discountedProductsInventoryData,
        `${sku}.${INVENTORY_ITEM_TRACKED}`,
        true
      )

      if (
        tracked &&
        getAvailabilityByRegion(
          customerCountryCode,
          sku,
          discountedProductsInventoryData
        ).isAvailable
      ) {
        availableSkus.push(sku)
      }
    })

    return availableSkus
  }, [
    discountedProductsInventoryData,
    discountedProductsData,
    customerLocation,
  ])

  const formattedItems = React.useMemo(() => {
    return items.map((item, i) => {
      const allDiscountProductsIndexIdMap = {}
      const allDiscountProducts: any = []
      const allDiscountBlockProps: any = []
      let decodedShopifyProductId

      try {
        decodedShopifyProductId = atob(item.shopifyProductId || "")
      } catch {
        decodedShopifyProductId = item.shopifyProductId
      }

      automaticDiscounts.forEach((automaticDiscount, i) => {
        const discountProducts: any = []
        let showDiscountBlock = false
        let discountType

        if (
          automaticDiscount.customerBuysProducts &&
          automaticDiscount.customerBuysProducts.indexOf(
            decodedShopifyProductId
          ) > -1
        ) {
          discountType = "X"
          automaticDiscount.customerGetsProducts.forEach((id) => {
            const contentfulId = automaticDiscount.idMap[id]

            if (discountedProductsData[contentfulId]) {
              discountProducts.push(discountedProductsData[contentfulId])
            }
          })

          let counterpartItemCount = 0

          discountProducts.forEach((dp) => {
            items
              .filter(
                ({ contentfulProductId }) =>
                  contentfulProductId === dp.contentful_id
              )
              .forEach((item) => {
                counterpartItemCount += item.quantity
              })
          })

          if (item.quantity > counterpartItemCount) {
            showDiscountBlock = true
          }
        } else if (
          automaticDiscount.customerGetsProducts &&
          automaticDiscount.customerGetsProducts.indexOf(
            decodedShopifyProductId
          ) > -1
        ) {
          discountType = "Y"
          automaticDiscount.customerBuysProducts.forEach((id) => {
            const contentfulId = automaticDiscount.idMap[id]

            if (discountedProductsData[contentfulId]) {
              discountProducts.push(discountedProductsData[contentfulId])
            }
          })

          let counterpartItemCount = 0

          discountProducts.forEach((dp) => {
            items
              .filter(
                ({ contentfulProductId }) =>
                  contentfulProductId === dp.contentful_id
              )
              .forEach((item) => {
                counterpartItemCount += item.quantity
              })
          })

          if (item.quantity > counterpartItemCount) {
            showDiscountBlock = true
          }
        }

        allDiscountProducts.push(discountProducts)
        allDiscountBlockProps.push({
          discountType,
          showDiscountBlock,
        })
        allDiscountProductsIndexIdMap[i] = automaticDiscount.id
      })

      allDiscountProducts.forEach((products, i) => {
        const discountId = allDiscountProductsIndexIdMap[i]
        const parsedDiscountId = discountId.replace(
          "gid://shopify/DiscountAutomaticNode/",
          ""
        )
        const discountConfig = automaticDiscountData?.find(
          ({ discountId }) => discountId === parsedDiscountId
        )

        if (
          discountConfig &&
          products.length > 0 &&
          allDiscountBlockProps[i] &&
          allDiscountBlockProps[i].discountType
        ) {
          allDiscountBlockProps[i].discountBlockCallout =
            discountConfig[
              `pdpMessage${allDiscountBlockProps[i].discountType}`
            ] || ""
          allDiscountBlockProps[i].discountBlockTitle =
            products.length === 1
              ? products[0].productName
              : allDiscountBlockProps[i].discountType === "X"
                ? discountConfig.selectXMessage
                : allDiscountBlockProps[i].discountType === "Y"
                  ? discountConfig.selectYMessage
                  : ""
          allDiscountBlockProps[i].discountImage =
            allDiscountBlockProps[i].discountType === "X"
              ? discountConfig.selectYImage
              : allDiscountBlockProps[i].discountType === "Y"
                ? discountConfig.selectXImage
                : ""
        }
      })

      const allFilteredDiscountProducts: any = []

      allDiscountProducts.forEach((discountProducts, i) => {
        let filteredDiscountProducts = discountProducts.filter(
          ({ variants }) => {
            return variants && variants.length > 0
          }
        )
        const discountId = allDiscountProductsIndexIdMap[i]
        const parsedDiscountId = discountId.replace(
          "gid://shopify/DiscountAutomaticNode/",
          ""
        )
        const discountConfig = automaticDiscountData?.find(
          ({ discountId }) => discountId === parsedDiscountId
        )

        if (!discountConfig) {
          return
        } else if (item.device === "Portable Charger") {
          filteredDiscountProducts = filteredDiscountProducts.filter((p) => {
            return !!p.builtInMagnet
          })
        } else if (discountConfig.matchDevice) {
          const hasMagnet = item.builtInMagnet

          filteredDiscountProducts = filteredDiscountProducts.filter((p) => {
            return hasMagnet
              ? p.device.name === item.device ||
                  p.device.name === "Portable Charger"
              : p.device.name === item.device
          })
        } else if (
          item?.productSubType &&
          ["cases", "screen-protectors"].indexOf(item?.productSubType) > -1
        ) {
          filteredDiscountProducts = filteredDiscountProducts.filter((p) => {
            return p.productSubType &&
              ["cases", "screen-protectors"].indexOf(p.productSubType.slug) > -1
              ? p.device.name === item.device
              : true
          })
        }

        allFilteredDiscountProducts.push(
          filteredDiscountProducts.map((p) => {
            const imageSrc = _.get(p, "variants[0].images[0].file.url", "")
            const [cleanedSrc] = imageSrc.split("?")
            const productImage = `https:${cleanedSrc}`
            const productPrice = _.get(p, "p.variants[0].price", "")
            const productCompareAtPrice = _.get(p, "variants[0].compareAtPrice")
            const marketPrices = _.get(p, "variants[0].marketPrices", {})

            return {
              ...p,
              marketPrices,
              productCompareAtPrice,
              productImage,
              productPrice,
            }
          })
        )
      })

      return {
        addDiscountedProductToCart: addToCart,
        automaticDiscountAllocations,
        availableSkus: availableSkusByRegion,
        discountBlockProps: allDiscountBlockProps,
        discountProducts: allDiscountProducts,
        item,
        lineIndex: i,
        onClickEdit: (e) => {
          e.stopPropagation()
          setCartItemToUpdate({ cartItem: item, lineIndex: i })
        },
        onRemoveCartItem,
        onUpdateCartItem,
        openProductSelector: allFilteredDiscountProducts.map(
          (discountProducts, i) => {
            if (discountProducts.length > 0) {
              return () => {
                const discountId = allDiscountProductsIndexIdMap[i]
                const parsedDiscountId = discountId.replace(
                  "gid://shopify/DiscountAutomaticNode/",
                  ""
                )
                const discountConfig = automaticDiscountData?.find(
                  ({ discountId }) => discountId === parsedDiscountId
                )

                setProductSelectorProducts(discountProducts)
                setProductSelectorDiscountConfig(discountConfig)
              }
            }

            return () => {}
          }
        ),
      }
    })
  }, [items, automaticDiscounts, discountedProductsData])

  return (
    <Backdrop $isOpen={isOpen}>
      <Container
        $disableClicks={showAddDiscount || showEditDiscount}
        $isOpen={isOpen}
        ref={cartRef}
      >
        <Header cartCount={cartCount} onRequestClose={onRequestClose} />
        {items.length ? (
          <Content>
            <Body items={formattedItems} />
            {/* CARTODO */}
            {/* {parseFloat(subtotal) !== parseFloat(total) && (
              <Row>
                <Text>Discount</Text>
                <Text>
                  {`- `}
                  {formatCurrency(
                    parseFloat(subtotal) - parseFloat(total),
                    "USD",
                    "en-US"
                  )}
                </Text>
              </Row>
            )} */}
            <DiscountButton
              discount={couponCodeApplied}
              openAddDiscount={openAddDiscount}
              openEditDiscount={openEditDiscount}
            />
            <Footer
              checkoutLink={checkoutLink}
              discount={couponCodeApplied}
              onAddDiscount={onUpdateDiscount}
              onAddDiscountSuccess={onAddDiscountSuccess}
              onRemoveDiscount={handleOnRemoveDiscount}
              showPromoToast={showPromoToast}
              subtotal={subtotal}
              total={total}
            />
          </Content>
        ) : (
          <EmptyCart recentItems={recentItems} />
        )}
      </Container>
      <EditCartItem
        containerRef={editCartItemRef}
        delta={delta}
        isOpen={!!cartItemToUpdate}
        onRemoveCartItem={handleOnRemoveCartItemMobile}
        onUpdateCartItem={handleOnUpdateCartItemMobile}
        product={cartItemToUpdate}
        setDelta={setDelta}
      />
      <AddDiscount
        containerRef={addDiscountRef}
        isOpen={showAddDiscount}
        onAddDiscount={onUpdateDiscount}
        onAddDiscountSuccess={onAddDiscountSuccess}
        onRequestClose={closeAddDiscount}
      />
      <EditDiscount
        containerRef={editDiscountRef}
        discount={couponCodeApplied}
        isOpen={showEditDiscount}
        onRemoveDiscount={handleOnRemoveDiscount}
        onRequestClose={closeEditDiscount}
      />
      <ProductSelector
        addToCart={addToCart}
        availableVariants={availableSkusByRegion}
        closeOnAddToCart
        containerRef={productSelectorRef}
        customerCountryCode={customerCountryCode}
        deal={productSelectorDiscountConfig?.pdpMessage || ""}
        header={productSelectorDiscountConfig?.pdpMessage || ""}
        isOpen={productSelectorProducts.length > 0}
        onRequestClose={closeProductSelector}
        products={productSelectorProducts}
        showPromoToast={true}
      />
    </Backdrop>
  )
}

export default Cart
